import { TOPIC_CATEGORIES } from '../../types'
import type { TopicFilters, TabCounts } from '../../types'

interface Props {
  filters: TopicFilters
  counts?: TabCounts
  onFilterChange: (filters: Partial<TopicFilters>) => void
}

export default function TopicFilterBar({ filters, counts, onFilterChange }: Props) {
  const activeType = filters.topic_type || null

  return (
    <div className="space-y-3 mb-4">
      <div className="flex items-center gap-1 bg-[#F5F5F5] p-1 rounded-lg w-fit">
        <button
          onClick={() => onFilterChange({ topic_type: null })}
          className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
            !activeType
              ? 'bg-white shadow-sm text-text-primary'
              : 'text-text-secondary hover:text-text-primary'
          }`}
        >
          All
        </button>
        <button
          onClick={() => onFilterChange({ topic_type: 'short_term' })}
          className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
            activeType === 'short_term'
              ? 'bg-white shadow-sm text-text-primary'
              : 'text-text-secondary hover:text-text-primary'
          }`}
        >
          Short Term
          {counts?.short_term != null && (
            <span className="ml-1.5 text-xs bg-charcoal/10 px-1.5 py-0.5 rounded-full">{counts.short_term}</span>
          )}
        </button>
        <button
          onClick={() => onFilterChange({ topic_type: 'long_term' })}
          className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
            activeType === 'long_term'
              ? 'bg-white shadow-sm text-text-primary'
              : 'text-text-secondary hover:text-text-primary'
          }`}
        >
          Camp
          {counts?.long_term != null && (
            <span className="ml-1.5 text-xs bg-charcoal/10 px-1.5 py-0.5 rounded-full">{counts.long_term}</span>
          )}
        </button>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search topics..."
            value={filters.search || ''}
            onChange={(e) => onFilterChange({ search: e.target.value || null })}
            className="w-full pl-9 pr-3 py-2 border border-stone rounded-md text-sm focus:outline-none focus:border-charcoal focus:ring-2 focus:ring-charcoal/15"
          />
        </div>

        <select
          value={filters.category || ''}
          onChange={(e) => onFilterChange({ category: e.target.value || null })}
          className="px-3 py-2 border border-stone rounded-md text-sm text-text-primary"
        >
          <option value="">All Categories</option>
          {TOPIC_CATEGORIES.filter(c => c.value !== 'none').map(c => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>

        <button
          type="button"
          onClick={() => onFilterChange({ priority: filters.priority === 'true' ? null : 'true' })}
          className={`px-3 py-2 rounded-md text-sm font-medium border transition-colors ${
            filters.priority === 'true'
              ? 'bg-[#ED1C24]/10 text-[#ED1C24] border-[#ED1C24]/30'
              : 'bg-white text-text-secondary border-stone hover:text-text-primary'
          }`}
          title="Show only priority topics"
        >
          Priority
        </button>

        <select
          value={filters.sort}
          aria-label="Sort"
          onChange={(e) => onFilterChange({ sort: e.target.value as TopicFilters['sort'] })}
          className="px-3 py-2 border border-stone rounded-md text-sm text-text-primary"
        >
          <option value="name">Name</option>
          <option value="manual">Manual Order</option>
          <option value="owner">Owner</option>
          <option value="priority">Priority</option>
          <option value="votes">Vote</option>
          <option value="date_created">Date Created</option>
        </select>
      </div>
    </div>
  )
}
